import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Send, ThumbsUp, MessageCircle, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Forum = ({ userId }) => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({
    category: 'Safe Nightlife',
    title: '',
    content: '',
    author: 'Anonymous'
  });

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async (category = null) => {
    try {
      const url = category ? `${API}/forum/posts?category=${encodeURIComponent(category)}` : `${API}/forum/posts`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load forum posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/forum/comments/${postId}`);
      setComments(response.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const handleUpvote = async (postId) => {
    try {
      await axios.post(`${API}/forum/posts/${postId}/upvote`);
      setPosts(posts.map(p => p.id === postId ? { ...p, upvotes: p.upvotes + 1 } : p));
    } catch (error) {
      console.error('Error upvoting post:', error);
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.title || !newPost.content) {
      toast.error('Please fill in all fields');
      return;
    }
    
    try {
      await axios.post(`${API}/forum/posts`, newPost);
      toast.success('Post created successfully!');
      setShowNewPost(false);
      setNewPost({ category: 'Safe Nightlife', title: '', content: '', author: 'Anonymous' });
      fetchPosts();
    } catch (error) {
      console.error('Error creating post:', error);
      toast.error('Failed to create post');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API}/forum/comments`, {
        post_id: selectedPost.id,
        content: newComment,
        author: 'Anonymous'
      });
      setNewComment('');
      fetchComments(selectedPost.id);
      setPosts(posts.map(p => p.id === selectedPost.id ? { ...p, comments_count: p.comments_count + 1 } : p));
      toast.success('Comment added!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const openPost = (post) => {
    setSelectedPost(post);
    fetchComments(post.id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl font-semibold">Loading forum...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-container p-6 md:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="rounded-full"
              data-testid="back-button-forum"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#1A1A1A] dark:text-[#FFFFFE]">
              Community Forum
            </h1>
          </div>
          <Dialog open={showNewPost} onOpenChange={setShowNewPost}>
            <DialogTrigger asChild>
              <Button
                className="bg-[#CCFF00] text-black font-bold px-6 py-3 rounded-full hover:scale-105 transition-transform"
                data-testid="new-post-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white dark:bg-[#232129] rounded-3xl max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Create New Post</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Category</label>
                  <select
                    value={newPost.category}
                    onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                    className="w-full bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl px-4 py-3 outline-none"
                    data-testid="post-category-select"
                  >
                    <option>Safe Nightlife</option>
                    <option>Overdose Prevention</option>
                    <option>Mental Health & Substance Use</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Title</label>
                  <Input
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    placeholder="What's your question or topic?"
                    className="bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl"
                    data-testid="post-title-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Content</label>
                  <Textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Share your thoughts..."
                    rows={6}
                    className="bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl"
                    data-testid="post-content-textarea"
                  />
                </div>
                <Button
                  onClick={handleCreatePost}
                  className="w-full bg-[#CCFF00] text-black font-bold px-8 py-4 rounded-full hover:scale-105 transition-transform"
                  data-testid="submit-post-button"
                >
                  Post Anonymously
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="all" className="mb-8" onValueChange={(value) => fetchPosts(value === 'all' ? null : value)}>
          <TabsList className="bg-white dark:bg-[#232129] p-2 rounded-2xl mb-6">
            <TabsTrigger value="all" className="rounded-xl" data-testid="forum-tab-all">All</TabsTrigger>
            <TabsTrigger value="Safe Nightlife" className="rounded-xl" data-testid="forum-tab-nightlife">Nightlife</TabsTrigger>
            <TabsTrigger value="Overdose Prevention" className="rounded-xl" data-testid="forum-tab-overdose">Overdose</TabsTrigger>
            <TabsTrigger value="Mental Health & Substance Use" className="rounded-xl" data-testid="forum-tab-mental-health">Mental Health</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-0">
            <div className="space-y-4">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-[#232129] rounded-3xl p-6 border border-black/5 dark:border-white/5 hover:border-[#CCFF00] transition-colors cursor-pointer"
                  onClick={() => openPost(post)}
                  data-testid={`forum-post-${post.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">
                        {post.title}
                      </h3>
                      <p className="text-[#5F6C7B] dark:text-[#94A1B2] line-clamp-2 mb-4">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpvote(post.id);
                          }}
                          className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2] hover:text-[#CCFF00] transition-colors"
                          data-testid={`upvote-button-${post.id}`}
                        >
                          <ThumbsUp className="w-5 h-5" />
                          <span className="font-medium">{post.upvotes}</span>
                        </button>
                        <div className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2]">
                          <MessageCircle className="w-5 h-5" />
                          <span className="font-medium">{post.comments_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="Safe Nightlife" className="mt-0">
            <div className="space-y-4">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-[#232129] rounded-3xl p-6 border border-black/5 dark:border-white/5 hover:border-[#CCFF00] transition-colors cursor-pointer"
                  onClick={() => openPost(post)}
                  data-testid={`forum-post-${post.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">
                        {post.title}
                      </h3>
                      <p className="text-[#5F6C7B] dark:text-[#94A1B2] line-clamp-2 mb-4">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpvote(post.id);
                          }}
                          className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2] hover:text-[#CCFF00] transition-colors"
                          data-testid={`upvote-button-${post.id}`}
                        >
                          <ThumbsUp className="w-5 h-5" />
                          <span className="font-medium">{post.upvotes}</span>
                        </button>
                        <div className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2]">
                          <MessageCircle className="w-5 h-5" />
                          <span className="font-medium">{post.comments_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="Overdose Prevention" className="mt-0">
            <div className="space-y-4">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-[#232129] rounded-3xl p-6 border border-black/5 dark:border-white/5 hover:border-[#CCFF00] transition-colors cursor-pointer"
                  onClick={() => openPost(post)}
                  data-testid={`forum-post-${post.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">
                        {post.title}
                      </h3>
                      <p className="text-[#5F6C7B] dark:text-[#94A1B2] line-clamp-2 mb-4">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpvote(post.id);
                          }}
                          className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2] hover:text-[#CCFF00] transition-colors"
                          data-testid={`upvote-button-${post.id}`}
                        >
                          <ThumbsUp className="w-5 h-5" />
                          <span className="font-medium">{post.upvotes}</span>
                        </button>
                        <div className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2]">
                          <MessageCircle className="w-5 h-5" />
                          <span className="font-medium">{post.comments_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="Mental Health & Substance Use" className="mt-0">
            <div className="space-y-4">
              {posts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white dark:bg-[#232129] rounded-3xl p-6 border border-black/5 dark:border-white/5 hover:border-[#CCFF00] transition-colors cursor-pointer"
                  onClick={() => openPost(post)}
                  data-testid={`forum-post-${post.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                          {post.category}
                        </span>
                        <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                          {post.author}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-[#1A1A1A] dark:text-[#FFFFFE] mb-2">
                        {post.title}
                      </h3>
                      <p className="text-[#5F6C7B] dark:text-[#94A1B2] line-clamp-2 mb-4">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-6">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleUpvote(post.id);
                          }}
                          className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2] hover:text-[#CCFF00] transition-colors"
                          data-testid={`upvote-button-${post.id}`}
                        >
                          <ThumbsUp className="w-5 h-5" />
                          <span className="font-medium">{post.upvotes}</span>
                        </button>
                        <div className="flex items-center gap-2 text-[#5F6C7B] dark:text-[#94A1B2]">
                          <MessageCircle className="w-5 h-5" />
                          <span className="font-medium">{post.comments_count}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
        <DialogContent className="bg-white dark:bg-[#232129] rounded-3xl max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedPost && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-[#CCFF00]/20 text-[#1A1A1A] dark:text-[#FFFFFE] text-xs font-bold rounded-full">
                    {selectedPost.category}
                  </span>
                  <span className="text-sm text-[#5F6C7B] dark:text-[#94A1B2]">
                    {selectedPost.author}
                  </span>
                </div>
                <DialogTitle className="text-2xl font-bold mb-3">{selectedPost.title}</DialogTitle>
                <p className="text-[#5F6C7B] dark:text-[#94A1B2] leading-relaxed">{selectedPost.content}</p>
              </DialogHeader>
              
              <div className="border-t border-black/10 dark:border-white/10 pt-6 mt-6">
                <h4 className="font-bold text-lg mb-4 text-[#1A1A1A] dark:text-[#FFFFFE]">
                  Comments ({comments.length})
                </h4>
                <div className="space-y-4 mb-6">
                  {comments.map((comment) => (
                    <div key={comment.id} className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-[#5F6C7B] dark:text-[#94A1B2]">
                          {comment.author}
                        </span>
                      </div>
                      <p className="text-[#1A1A1A] dark:text-[#FFFFFE]">{comment.content}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-3">
                  <Textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    rows={3}
                    className="flex-1 bg-gray-50 dark:bg-gray-900 border-2 border-transparent focus:border-[#CCFF00] rounded-xl"
                    data-testid="comment-textarea"
                  />
                  <Button
                    onClick={handleAddComment}
                    className="bg-[#CCFF00] text-black font-bold px-6 rounded-full hover:scale-105 transition-transform"
                    data-testid="submit-comment-button"
                  >
                    <Send className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Forum;
